import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-[41px]">
      {/* Simple Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8 text-center">
            {/* Page Title */}
            <h1 className="text-3xl font-semibold text-gray-600 tracking-wide mb-4">PRIVACY POLICY</h1>
            {/* Breadcrumb Navigation */}
            <nav className="flex items-center justify-center space-x-2 text-sm" aria-label="Breadcrumb">
              <Link 
                to="/" 
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                Home
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">Privacy Policy</span>
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8">
          
          {/* Privacy Policy Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Privacy Policy</h2>
            <p className="text-sm text-gray-600 mb-6">
              <strong>Effective Date:</strong> 01/03/2025
            </p>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              At Signature Styles, we respect your privacy and are committed to protecting your personal information. 
              This Privacy Policy outlines how we collect, use, and safeguard your data when you visit our website 
              (www.signaturestylesmm.com).
            </p>

            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Information We Collect</h3>
                <p className="text-gray-700 mb-3">We collect the following types of information:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Personal Information:</strong> Name, phone number, email address, shipping address, and payment details when you place an order.</li>
                  <li><strong>Browsing Data:</strong> IP address, browser type, and device information to improve website performance.</li>
                  <li><strong>Communication Data:</strong> Messages, feedback, and inquiries sent via our contact forms or customer support.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">How We Use Your Information</h3>
                <p className="text-gray-700 mb-3">We use your data to:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Process and deliver orders.</li>
                  <li>Improve your shopping experience.</li>
                  <li>Send promotional offers (only if you subscribe).</li>
                  <li>Respond to customer service inquiries.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Data Protection</h3>
                <p className="text-gray-700">
                  We take all necessary security measures to protect your data. Payment information is encrypted and 
                  processed securely through trusted payment providers.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Sharing of Information</h3>
                <p className="text-gray-700 mb-3">We do not sell or rent your personal data. However, we may share it with:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Trusted partners (such as delivery services) to fulfill your order.</li>
                  <li>Legal authorities if required by law.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Cookies & Tracking Technologies</h3>
                <p className="text-gray-700">
                  We use cookies to enhance your browsing experience. You can disable cookies in your browser settings, 
                  but some site features may not function properly.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Your Rights</h3>
                <p className="text-gray-700 mb-3">You have the right to:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Request access to your personal data.</li>
                  <li>Request corrections to incorrect data.</li>
                  <li>Opt out of promotional emails at any time.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Contact Us</h3>
                <p className="text-gray-700">
                  For any privacy-related inquiries, please contact us at{' '}
                  <a href="mailto:support@signaturestylesmm.com" className="text-blue-600 hover:text-blue-800 underline">
                    support@signaturestylesmm.com
                  </a>.
                </p>
              </div>
            </div>
          </div>

          {/* Store Policy Section */}
          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Store Policy</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Order & Payment</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Accepted payment methods: KBZ Bank, YOMA Bank, AYA Bank, UAB Bank, CB Bank, KBZ Pay, Wave Pay, AYA Pay, CB Pay, UAB Pay, OK$.</li>
                  <li>Once an order is confirmed, it cannot be canceled.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Shipping & Delivery</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>We only offer local shipping within Myanmar.</li>
                  <li>Orders are processed within 1 business day and delivered within 2-7 days.</li>
                  <li>Delivery charges depend on location and order size.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Exchange Policy</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>We do not offer returns or refunds. However, exchanges are allowed.</li>
                  <li>Customers must request an exchange within 2 days of receiving the product.</li>
                  <li>Exchanges are only accepted for size or color changes (subject to stock availability).</li>
                  <li>Products must be in original, unused condition with tags attached.</li>
                  <li>Customers are responsible for return shipping costs.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Warranty & Product Quality</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>All products are inspected for quality before shipping.</li>
                  <li>We do not offer warranties, but we guarantee high-quality first-copy branded items.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Customer Support</h3>
                <p className="text-gray-700">
                  For any order-related inquiries, please contact us at{' '}
                  <a href="mailto:support@signaturestylesmm.com" className="text-blue-600 hover:text-blue-800 underline">
                    support@signaturestylesmm.com
                  </a>.
                </p>
              </div>
            </div>
          </div>

          {/* Back to Home Link */}
          <div className="border-t border-gray-200 pt-8 mt-12 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center px-6 py-3 bg-gray-800 text-white font-medium text-sm tracking-wide hover:bg-gray-700 transition-colors rounded-md"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
